import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiService } from '../services/api.service';
import type { UserResponseDTO, UserCreateDTO, UserLoginDTO } from '@shared/index.js';
import toast from 'react-hot-toast';

interface AuthContextType {
  user: UserResponseDTO | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (data: UserLoginDTO) => Promise<void>;
  register: (data: UserCreateDTO) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<UserResponseDTO | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshUser = async () => {
    try {
      const { user } = await apiService.getMe();
      setUser(user);
    } catch (error) {
      setUser(null);
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('auth_token');
      if (token) {
        await refreshUser();
      }
      setIsLoading(false);
    };

    initAuth();
  }, []);

  const login = async (data: UserLoginDTO) => {
    try {
      const response = await apiService.login(data);
      setUser(response.user);
      toast.success('Logged in successfully!');
    } catch (error: unknown) {
      toast.error('Failed to login. Please check your credentials.');
      throw error;
    }
  };

  const register = async (data: UserCreateDTO) => {
    try {
      const response = await apiService.register(data);
      setUser(response.user);
      toast.success('Account created successfully!');
    } catch (error: unknown) {
      toast.error('Failed to create account. Please try again.');
      throw error;
    }
  };

  const logout = () => {
    apiService.logout();
    setUser(null);
    toast.success('Logged out successfully');
  };

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    register,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
